import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { cartQuantityForVariant } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Called when the component is connected to the DOM
   */
  connectedCallback() {
    super.connectedCallback();
    this.#applyQuantityRules();
    // Initialize button states when component is first loaded
    this.#updateButtonStates();

    if (!this.#isCartLine) {
      document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
        signal: this.abortController.signal,
      });
    }
  }

  /**
   * A variant change morphs this input rather than replacing it, so no connect runs and the
   * server's values stand — including a quantity the cart has no room for.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#applyQuantityRules();
    this.#updateButtonStates();
  }

  /**
   * Public sync hook for callers that set quantity programmatically.
   */
  syncButtonStates() {
    this.#checkQuantityRules();
    this.#updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * On Enter in quantity input: commit and run update (AJAX).
   * @param {KeyboardEvent} event
   */
  handleQuantityKeydown(event) {
    if (event.key !== 'Enter') return;
    if (!(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const input = this.refs.quantityInput;
    const prevValue = input.value;
    const newValue = event.target.value;
    input.value = newValue;
    if (String(prevValue).trim() !== String(newValue).trim()) this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.#updateButtonStates();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Updates the state of increase/decrease buttons based on current quantity
   */
  #updateButtonStates() {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value);
    const min = Number(quantityInput.min || 1);
    const max = Number(quantityInput.max) || Infinity;

    const minusButton = this.querySelector('.quantity-minus');
    const plusButton = this.querySelector('.quantity-plus');

    if (minusButton) {
      minusButton.disabled = currentValue <= min;
    }

    if (plusButton) {
      plusButton.disabled = currentValue >= max;
    }
  }

  /**
   * Whether this edits an existing cart line. The rules below never touch those: resetting the
   * input would silently rewrite a quantity the shopper already committed to.
   */
  get #isCartLine() {
    return Boolean(this.refs.quantityInput?.dataset.cartLine);
  }

  /**
   * Whether the variant carries a rule worth enforcing. Liquid decides and marks the input, so
   * this does not have to infer it from the attributes it happens to render.
   */
  get #hasQuantityRule() {
    return this.refs.quantityInput?.hasAttribute('data-quantity-rule') ?? false;
  }

  /**
   * Applies the variant's quantity rule against what the cart already holds: a maximum counts the
   * cart too, and once the cart covers the minimum the next step down is a single increment.
   *
   * Ported from Dawn's `setQuantityBoundries`, with one correction: Dawn drops the max attribute
   * when the allowance hits 0, re-opening a variant the cart is already full of.
   */
  #applyQuantityRules() {
    if (this.#isCartLine || !this.#hasQuantityRule) return;

    const input = this.refs.quantityInput;
    const cartQuantity = parseInt(input.dataset.cartQuantity ?? '0', 10) || 0;
    const ruleMin = parseInt(input.dataset.min ?? '1', 10) || 1;
    const ruleMax = input.dataset.max ? parseInt(input.dataset.max, 10) : null;
    const step = parseInt(input.step, 10) || 1;

    let min = ruleMin;
    const max = ruleMax === null ? null : ruleMax - cartQuantity;

    if (max !== null) min = Math.min(min, max);
    if (cartQuantity >= ruleMin) min = Math.min(min, step);

    input.min = String(min);
    input.value = String(min);

    if (max === null) {
      input.removeAttribute('max');
    } else {
      input.max = String(max);
    }

    this.#syncAddToCart(max);
  }

  /**
   * Locks add to cart once the cart holds all the rule allows — nothing is left to submit. The
   * label stays: the product is not sold out, and the rules line above already says why.
   *
   * Only a button this disabled is released again, so one the server disabled stays that way.
   *
   * @param {number | null} allowance - How many more the rule permits, or null when uncapped.
   */
  #syncAddToCart(allowance) {
    const form = this.closest('product-form-component');
    const button = /** @type {HTMLButtonElement | null} */ (
      form?.querySelector('[ref="addToCartButton"][data-product-information]')
    );
    if (!button) return;

    // Express checkout would submit the same empty quantity, so it goes with the button.
    const acceleratedCheckout = form?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    const blocked = allowance !== null && allowance <= 0;

    if (blocked) {
      button.dataset.quantityBlocked = 'true';
      button.disabled = true;
      acceleratedCheckout?.setAttribute('hidden', '');
      return;
    }

    if (button.dataset.quantityBlocked !== 'true') return;

    delete button.dataset.quantityBlocked;
    button.disabled = false;
    acceleratedCheckout?.removeAttribute('hidden');
  }

  /**
   * Re-reads the cart quantity, then re-applies the rules. Dawn re-renders the whole section here.
   */
  #onCartUpdate = async () => {
    if (!this.#hasQuantityRule) return;

    const input = this.refs.quantityInput;
    const variantId = input.dataset.variantId;
    if (!variantId) return;

    try {
      input.dataset.cartQuantity = String(await cartQuantityForVariant(variantId));
    } catch {
      // Keep the rules as rendered if the cart can't be read.
      return;
    }

    this.#applyQuantityRules();
    this.#updateButtonStates();
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
